<script setup lang="ts">
import { PLATFORM } from '~/utils/platform'
const { t } = useLocale()
const auth = useAuth()
const nudge = useNativeIdentityNudge()
const { visible } = nudge
const dialog = ref<HTMLElement | null>(null)
let previousFocus: HTMLElement | null = null

function close() {
  if (auth.busy.value) return
  nudge.dismiss()
}
const dismiss = useBackdropDismiss(close)
// This app-wide modal stays above covers mounted during an earn's route change.
useBackLayer('identity-nudge', () => visible.value, () => {
  if (auth.busy.value) return false
  close()
}, 1)
watch(() => auth.user.value, (user) => { if (user && !user.anonymous) nudge.dismiss() })

function onKeydown(event: KeyboardEvent) {
  if (!visible.value) return
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    close()
    return
  }
  if (event.key !== 'Tab' || !dialog.value) return
  // ModalClose teleports onto the backdrop, so include it in the focus loop.
  const controls = [...dialog.value.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]')]
    .filter(element => element.getClientRects().length)
  const first = controls[0], last = controls[controls.length - 1]
  if (!first || !last) { event.preventDefault(); dialog.value.focus(); return }
  const active = document.activeElement
  if (!controls.some(element => element === active)) {
    event.preventDefault(); (event.shiftKey ? last : first).focus()
  } else if (event.shiftKey && active === first) {
    event.preventDefault(); last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault(); first.focus()
  }
}
watch(visible, async (open) => {
  if (open) {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    await nextTick()
    dialog.value?.focus({ preventScroll: true })
  } else if (previousFocus?.isConnected) {
    previousFocus.focus({ preventScroll: true })
  }
})
onMounted(() => {
  if (visible.value) dialog.value?.focus({ preventScroll: true })
  document.addEventListener('keydown', onKeydown, true)
})
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown, true))
</script>

<template>
  <div
    v-if="visible"
    ref="dialog"
    class="standard-modal-backdrop native-identity-nudge"
    role="dialog"
    aria-modal="true"
    aria-labelledby="native-identity-title"
    tabindex="-1"
    data-testid="native-identity-nudge"
    @pointerdown="dismiss.down"
    @pointerup="dismiss.up"
    @pointercancel="dismiss.cancel"
  >
    <div class="card card-ornate standard-modal-card native-identity-card">
      <ModalClose testid="native-identity-close" :disabled="auth.busy.value" @close="close" />
      <div class="standard-modal-scroll">
        <h2 id="native-identity-title">{{ t('nativeIdentity.title') }}</h2>
        <p v-if="nudge.reason.value === 'recovery'">{{ t('nativeIdentity.recovery') }}</p>
        <!-- 🍎 M7: Apple first on the iPhone (App Review 4.8). -->
        <button v-if="PLATFORM === 'ios'" type="button" class="btn-apple" :disabled="auth.busy.value" data-testid="native-identity-sign-in-apple" @click="auth.signIn('apple')"><span class="btn-apple-logo" aria-hidden="true"></span>{{ t('title.signInApple') }}</button>
        <button type="button" class="btn btn-secondary btn-bar" :disabled="auth.busy.value" data-testid="native-identity-sign-in" @click="auth.signIn('google')">{{ t(PLATFORM === 'ios' ? 'title.signInGoogle' : 'title.signIn') }}</button>
        <button type="button" class="btn btn-secondary btn-bar" :disabled="auth.busy.value" data-testid="native-identity-later" @click="close">{{ t('nativeIdentity.later') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.native-identity-nudge { z-index: 110; }
</style>
